import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/dashboard/widgets/shared/StatusBadge";
import { formatNOK } from "@/lib/format";
import { SERVICE_STATUS_LABELS } from "@/lib/equipment/constants";
import type { DBEquipmentServiceTicket } from "@/lib/db-types";

interface ServiceTableProps {
  tickets: DBEquipmentServiceTicket[];
  /** Kostnad-kolonnen vises bare for admin. */
  showCost: boolean;
  /** Admin og utstyrsansvarlige kan endre saken. */
  onEdit?: (ticket: DBEquipmentServiceTicket) => void;
}

const th = "py-2 px-3 font-medium text-muted-foreground text-xs uppercase tracking-wider whitespace-nowrap";

function StatusPill({ status }: { status: DBEquipmentServiceTicket["status"] }) {
  const label = SERVICE_STATUS_LABELS[status];
  if (status === "resolved") return <StatusBadge tone="reached" label={label} />;
  if (status === "at_vendor") return <StatusBadge tone="miss" label={label} />;
  return <StatusBadge tone="near" label={label} />;
}

/** «Meldt» som i mockup I: 23.10 · Mariann. */
function reportedLabel(t: DBEquipmentServiceTicket): string {
  const d = new Date(t.created_at);
  const day = `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
  const first = t.reported_by_name?.split(" ")[0];
  return first ? `${day} · ${first}` : day;
}

/** Servicesakene (mockup I): utstyr, meldt, beskrivelse, status og kostnad. */
export function ServiceTable({ tickets, showCost, onEdit }: ServiceTableProps) {
  if (tickets.length === 0) return <p className="text-sm text-muted-foreground p-4">Ingen servicesaker.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs sm:text-sm">
        <thead>
          <tr className="border-b">
            <th className={`${th} text-left`}>Utstyr</th>
            <th className={`${th} text-left`}>Meldt</th>
            <th className={`${th} text-left`}>Beskrivelse</th>
            <th className={`${th} text-left`}>Verksted</th>
            <th className={`${th} text-left`}>Status</th>
            {showCost && <th className={`${th} text-right`}>Kostnad</th>}
            {onEdit && <th className={th}><span className="sr-only">Valg</span></th>}
          </tr>
        </thead>
        <tbody>
          {tickets.map((t) => (
            <tr key={t.id} className="border-b border-border/50 last:border-0 hover:bg-muted/30">
              <td className="py-2.5 px-3">
                <Link to={`/utstyr/${t.item_id}`} className="font-medium hover:underline">{t.item_name}</Link>
                <span className="block text-xs text-muted-foreground">{t.item_department_name ?? "Ukjent avdeling"}</span>
              </td>
              <td className="py-2.5 px-3 whitespace-nowrap">{reportedLabel(t)}</td>
              <td className="py-2.5 px-3">
                {!t.usable && <span className="font-medium">Ikke brukbar, </span>}
                {t.description}
              </td>
              <td className="py-2.5 px-3">{t.vendor ?? "–"}</td>
              <td className="py-2.5 px-3"><StatusPill status={t.status} /></td>
              {showCost && <td className="py-2.5 px-3 text-right tabular-nums">{t.cost != null ? formatNOK(t.cost) : "–"}</td>}
              {onEdit && (
                <td className="py-2.5 px-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => onEdit(t)} aria-label={`Endre servicesak for ${t.item_name}`}>
                    Endre
                  </Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
